import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "./Reveal";
import { BRAND } from "@/data/content";

const HERO_IMG = "https://images.pexels.com/photos/10426651/pexels-photo-10426651.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
    window.dispatchEvent(new CustomEvent("edc:set-inquiry", { detail: "Book a Strategy Session" }));
  };
  const scrollToApproach = () => document.querySelector("#approach")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative h-[100svh] min-h-[680px] overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Architectural structure" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40 pointer-events-none" />

      <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pt-24 md:pt-28 pb-14 md:pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="overline text-[#d4af37] mb-8"
          data-testid="hero-positioning"
        >
          {BRAND.positioning}
        </motion.p>

        <h1 className="font-serif font-light leading-[0.98] tracking-tight text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.4rem] max-w-5xl">
          <MaskLine delay={0.35}>{BRAND.tagline[0]}</MaskLine>
          <MaskLine delay={0.5} className="text-[#d4af37] italic">
            {BRAND.tagline[1]}
          </MaskLine>
          <MaskLine delay={0.65}>{BRAND.tagline[2]}</MaskLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="text-white/70 text-base md:text-lg font-light leading-relaxed max-w-xl"
          >
            {BRAND.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="flex items-center gap-4 shrink-0"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={scrollToContact}
              className="btn-gold rounded-full px-8 py-4 text-xs font-semibold tracking-wider uppercase"
            >
              Book a Strategy Session
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={scrollToApproach}
              className="btn-ghost rounded-full px-8 py-4 text-xs font-semibold tracking-wider uppercase"
            >
              Our Approach
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-3 text-white/50"
      >
        <span className="overline">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
